import { baseApi } from "@/services/api";

export type MusicUploadReviewStatus =
  | "pending-review"
  | "approved"
  | "rejected"
  | "revoked"
  | "upload-initiated"
  | string;

export type OversightBucket =
  | "all"
  | "needs-attention"
  | "flagged-security"
  | "blocked"
  | "admitted";

export type OversightAttentionKind =
  | "staging-auto-admit-gate"
  | "policy-hold"
  | "catalog-link"
  | "metadata-review"
  | "technical-failure"
  | "bridge-failed"
  | "deferred-zip"
  | "malware"
  | "blocked"
  | "needs-attention"
  | string
  | null;

export interface ProcessingJobOversightSnapshot {
  jobId: string | null;
  state: string | null;
  decision: string | null;
  reasonCode: string | null;
  failedCheckpoint: string | null;
  currentCheckpoint: string | null;
  uploadSource: string | null;
  malwareStatus: string | null;
  policyStatus: string | null;
  catalogLinkStatus: string | null;
  audioStatus: string | null;
  metadataConfidence: number | null;
  identificationSummary: unknown;
  trackId: string | null;
  approvedArtifactVersionId: string | null;
  discoveredTitle: string | null;
  discoveredArtist: string | null;
  originalFilename: string | null;
  updatedAt: string | null;
  createdAt: string | null;
}

export interface MusicUploadReviewRow {
  uploadId: string;
  sourceMasterAccountId: string;
  sourceUserId: string | null;
  sourceUsername: string | null;
  companyName: string | null;
  status: MusicUploadReviewStatus;
  fileName: string;
  fileSizeBytes: number | null;
  contentType: string | null;
  detectedAudioFormat: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  reviewedAt: string | null;
  reviewerUserId: string | null;
  rejectionReason: string | null;
  s3Bucket: string | null;
  s3Key: string | null;
  processingJobId?: string | null;
  bridgeStatus?: string | null;
  bridgedAt?: string | null;
  bridgeError?: string | null;
  processingObjectKey?: string | null;
  oversightBucket?: OversightBucket | string;
  attentionKind?: OversightAttentionKind;
  routineHoldingApproveAllowed?: boolean;
  displayLabel?: string;
  processingJob?: ProcessingJobOversightSnapshot | null;
}

export interface MusicUploadReviewHistoryEvent {
  eventId: string | null;
  eventType: string | null;
  eventTimestamp: string | null;
  actorSub: string | null;
  actorRole: string | null;
  beforeStatus: string | null;
  afterStatus: string | null;
  rejectionReason: string | null;
}

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  message?: string;
}

function unwrap<T>(raw: ApiEnvelope<T>, fallbackMessage: string): T {
  if (!raw || raw.success !== true || !raw.data) {
    throw new Error(raw?.message || fallbackMessage);
  }
  return raw.data;
}

export const musicUploadReviewsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    listMusicUploadReviews: builder.query<
      {
        uploads: MusicUploadReviewRow[];
        statusFilter: string;
        oversightBucketFilter?: string;
        catalogPromotion: {
          catalogWritesAllowed: boolean;
          finalizeSeamAllowed: boolean;
          processingJobAllowed: boolean;
        };
      },
      { status?: string; oversightBucket?: OversightBucket | string } | void
    >({
      query: (args) => {
        const status =
          args && "status" in args && args.status ? args.status : "all";
        const oversightBucket =
          args && "oversightBucket" in args && args.oversightBucket
            ? args.oversightBucket
            : "all";
        const qs = new URLSearchParams({
          status: String(status),
          oversightBucket: String(oversightBucket),
        });
        return {
          url: `/sa/music-upload-reviews?${qs.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (raw: ApiEnvelope<any>) =>
        unwrap(raw, "Failed to list music provider uploads"),
      providesTags: (result) =>
        result?.uploads
          ? [
              ...result.uploads.map((u) => ({
                type: "MusicUploadReviews" as const,
                id: u.uploadId,
              })),
              { type: "MusicUploadReviews" as const, id: "LIST" },
            ]
          : [{ type: "MusicUploadReviews" as const, id: "LIST" }],
    }),

    getMusicUploadReview: builder.query<
      {
        upload: MusicUploadReviewRow;
        reviewHistory: MusicUploadReviewHistoryEvent[];
        catalogPromotion: {
          catalogWritesAllowed: boolean;
          finalizeSeamAllowed: boolean;
          processingJobAllowed: boolean;
        };
      },
      string
    >({
      query: (uploadId) => ({
        url: `/sa/music-upload-reviews/${encodeURIComponent(uploadId)}`,
        method: "GET",
      }),
      transformResponse: (raw: ApiEnvelope<any>) =>
        unwrap(raw, "Failed to load music provider upload"),
      providesTags: (_r, _e, uploadId) => [
        { type: "MusicUploadReviews", id: uploadId },
      ],
    }),
  }),
});

export const {
  useListMusicUploadReviewsQuery,
  useGetMusicUploadReviewQuery,
  useLazyGetMusicUploadReviewQuery,
} = musicUploadReviewsApi;
